import React from 'react';
import { isWeekend, isHoliday } from '../utils/holidays';
import * as XLSX from 'xlsx';

function Summary({ month, year, registrations, userSettings }) {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

  // Get all days in the month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthRegistrations = registrations[monthKey] || {};

  let ovDays = 0;
  let freeDays = 0;
  let bikeDays = 0;
  let homeDays = 0;
  let ziekDays = 0;
  let weekendDays = 0;
  let holidayDays = 0;
  let notFilledDays = 0;

  const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
  const defaultThuiswerkDagen = userSettings.defaultThuiswerkDagen || [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(Date.UTC(year, month, day));
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isWerkdag = werkdagen.includes(date.getDay());
    const isAutoVrij = isWeekend(date) || isHoliday(date, year) || !isWerkdag;
    const dayNum = date.getDay();
    const customDefault = (userSettings.defaultRegistratieDagen || {})[dayNum];
    const defaultDag = !isAutoVrij
      ? (customDefault !== undefined ? customDefault : (defaultThuiswerkDagen.includes(dayNum) ? 'thuis' : ''))
      : '';
    const registration = monthRegistrations[dateString] ?? (isAutoVrij ? 'vrij' : defaultDag);

    if (isWeekend(date)) {
      weekendDays++;
      if (isHoliday(date, year)) {
        holidayDays++;
      }
    } else if (isHoliday(date, year)) {
      holidayDays++;
    } else {
      // Workday
      switch (registration) {
        case 'ov':
          ovDays++;
          break;
        case 'vrij':
          freeDays++;
          break;
        case 'fiets':
        case 'auto':
          bikeDays++;
          break;
        case 'thuis':
          homeDays++;
          break;
        case 'ziek':
          ziekDays++;
          break;
        default:
          notFilledDays++;
          break;
      }
    }
  }

  const totalWorkdays = daysInMonth - weekendDays - holidayDays;
  const filledWorkdays = totalWorkdays - notFilledDays;

  const downloadExcel = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const rows = [
      [`Samenvatting - ${maandNaam}`],
      [],
      ['Categorie', 'Aantal dagen'],
      ['OV-dagen', ovDays],
      ['Vrije dagen', freeDays],
      ['Fiets-/autodagen', bikeDays],
      ['Thuiswerkdagen', homeDays],
      ['Ziekdagen', ziekDays],
      ['Weekenddagen', weekendDays],
      ['Feestdagen', holidayDays],
      ['Niet ingevuld (werkdagen)', notFilledDays],
      [],
      ['Totaal dagen in maand', daysInMonth],
      ['Werkdagen', totalWorkdays],
      ['Weekend- en feestdagen', weekendDays + holidayDays],
      ['Ingevuld', filledWorkdays],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 28 }, { wch: 16 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Samenvatting');
    XLSX.writeFile(wb, `Samenvatting_${maandNaam.replace(' ', '_')}.xlsx`);
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Samenvatting - {new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
          </h2>
          <button onClick={downloadExcel} className="flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Excel
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{ovDays}</div>
            <div className="text-sm text-green-800">OV-dagen</div>
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{bikeDays}</div>
            <div className="text-sm text-orange-800">Fiets-/autodagen</div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{homeDays}</div>
            <div className="text-sm text-blue-800">Thuiswerkdagen</div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{freeDays}</div>
            <div className="text-sm text-yellow-800">Niet werkdagen</div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{ziekDays}</div>
            <div className="text-sm text-red-800">Ziekdagen</div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">{weekendDays}</div>
            <div className="text-sm text-gray-800">Weekenddagen</div>
          </div>

          <div className="bg-pink-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-pink-600">{holidayDays}</div>
            <div className="text-sm text-pink-800">Feestdagen</div>
          </div>
        </div>

        <div className="mt-6 text-sm text-gray-600">
          <p><strong>Totaal dagen in maand:</strong> {daysInMonth}</p>
          <p><strong>Werkdagen:</strong> {totalWorkdays}</p>
          <p>
            <strong>Ingevuld:</strong> {filledWorkdays} van {totalWorkdays} werkdagen
            {notFilledDays === 0
              ? <span className="ml-2 text-green-600 font-semibold">✓ Alles ingevuld</span>
              : null}
          </p>
          <p><strong>Nog in te vullen:</strong> {notFilledDays === 0 ? '—' : `${notFilledDays} dag${notFilledDays !== 1 ? 'en' : ''}`}</p>
          <p><strong>Weekend- en feestdagen:</strong> {weekendDays + holidayDays}</p>
        </div>
      </div>
    </div>
  );
}

export default Summary;